import React, { useState, useEffect } from 'react';
import { X, Eye, Download, Send, Check } from 'lucide-react';
import { reportCardService } from '../../../services/index.js';

const ReportCardStudentDrawer = ({
  reportCard = null,
  onClose,
  onOpenFullPreview,
  onDownloadPDF,
  onPublish,
  onSaveRemarksSuccess,
  showSuccessNotification,
  showErrorNotification
}) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [classRemarks, setClassRemarks] = useState('');
  const [principalRemarks, setPrincipalRemarks] = useState('');
  const [savingRemarks, setSavingRemarks] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'marks' | 'remarks'

  useEffect(() => {
    if (reportCard && reportCard.id) {
      loadDetails(reportCard.id);
      setClassRemarks(reportCard.classTeacherRemarks || '');
      setPrincipalRemarks(reportCard.principalRemarks || '');
    }
  }, [reportCard]);

  const loadDetails = async (id) => {
    setLoading(true);
    try {
      const res = await reportCardService.getReportCardById(id);
      setDetails(res);
      if (res.reportCard) {
        setClassRemarks(res.reportCard.classTeacherRemarks || '');
        setPrincipalRemarks(res.reportCard.principalRemarks || '');
      }
    } catch (err) {
      console.error('Failed to load report card details for drawer:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!reportCard) return null;

  const rc = details?.reportCard || reportCard;
  const entries = details?.entries || [];
  const student = rc.student || {};
  const studentName = student.name || rc.studentName || 'Student';
  const pct = rc.percentage !== undefined ? rc.percentage : 0;

  const handleSaveRemarks = async () => {
    setSavingRemarks(true);
    try {
      const res = await reportCardService.updateRemarks(rc.id, classRemarks, principalRemarks);
      if (res && res.success) {
        showSuccessNotification('Remarks Saved', 'Report card remarks updated successfully');
        if (typeof onSaveRemarksSuccess === 'function') {
          onSaveRemarksSuccess();
        }
      } else {
        showErrorNotification('Save Failed', res.error || 'Failed to save remarks');
      }
    } catch (err) {
      showErrorNotification('Error', err.message);
    } finally {
      setSavingRemarks(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(15, 23, 42, 0.45)',
      backdropFilter: 'blur(2px)',
      zIndex: 1040,
      display: 'flex',
      justifyContent: 'flex-end'
    }}>
      <div style={{
        width: '480px',
        maxWidth: '92vw',
        background: '#ffffff',
        height: '100%',
        boxShadow: '-4px 0 25px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'slideLeft 0.2s ease-out'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1.5px solid #e2e8f0',
          background: '#f8fafc',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '800', color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Student Report Card
            </div>
            <h3 style={{ margin: '2px 0 0 0', fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>
              {studentName}
            </h3>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
              {student.className || rc.className} {student.section ? `• Section ${student.section}` : ''} | ID: {student.studentId || rc.studentId}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              padding: '6px',
              cursor: 'pointer',
              color: '#64748b'
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Sub-tab Navigation */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e2e8f0',
          background: '#ffffff',
          padding: '0 16px'
        }}>
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'marks', label: `Subject Marks (${entries.length})` },
            { key: 'remarks', label: 'Remarks & Signoff' }
          ].map(t => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              style={{
                padding: '10px 14px',
                fontSize: '12.5px',
                fontWeight: activeTab === t.key ? '800' : '600',
                color: activeTab === t.key ? '#2563eb' : '#64748b',
                border: 'none',
                borderBottom: activeTab === t.key ? '2px solid #2563eb' : '2px solid transparent',
                background: 'none',
                cursor: 'pointer'
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', fontSize: '13px' }}>
              Loading student report card...
            </div>
          ) : (
            <>
              {/* TAB 1: OVERVIEW */}
              {activeTab === 'overview' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  
                  {/* Overall Result Hero Box */}
                  <div style={{
                    background: rc.result === 'PASS' ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' : 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                    borderRadius: '10px',
                    padding: '16px 20px',
                    border: rc.result === 'PASS' ? '1.5px solid #bbf7d0' : '1.5px solid #fecaca',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: '800', color: rc.result === 'PASS' ? '#166534' : '#991b1b', textTransform: 'uppercase' }}>
                        Overall Performance
                      </div>
                      <div style={{ fontSize: '28px', fontWeight: '800', color: rc.result === 'PASS' ? '#166534' : '#991b1b', marginTop: '2px' }}>
                        {pct.toFixed(1)}%
                      </div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: rc.result === 'PASS' ? '#15803d' : '#b91c1c' }}>
                        Total: {rc.obtainedMarks} / {rc.totalMarks} Marks
                      </div>
                    </div>

                    <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '800',
                        background: rc.result === 'PASS' ? '#166534' : '#991b1b',
                        color: '#ffffff'
                      }}>
                        {rc.result || 'PASS'}
                      </span>
                      <span style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>
                        Grade: {rc.overallGrade || '—'}
                      </span>
                    </div>
                  </div>

                  {/* Summary Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b' }}>ATTENDANCE</div>
                      <div style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', marginTop: '2px' }}>
                        {student.attendancePercentage || 92}%
                      </div>
                    </div>
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b' }}>STATUS</div>
                      <div style={{ fontSize: '14px', fontWeight: '800', color: rc.status === 'PUBLISHED' ? '#166534' : '#2563eb', marginTop: '2px' }}>
                        {rc.status}
                      </div>
                    </div>
                  </div>

                  {/* Subjects Quick Table */}
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                    <div style={{ padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '11.5px', fontWeight: '800', color: '#475569', textTransform: 'uppercase' }}>
                      Subject Scores
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ background: '#f1f5f9', color: '#475569', textAlign: 'left' }}>
                          <th style={{ padding: '6px 10px', fontWeight: '700' }}>Subject</th>
                          <th style={{ padding: '6px 10px', textAlign: 'center', fontWeight: '700' }}>Marks</th>
                          <th style={{ padding: '6px 10px', textAlign: 'center', fontWeight: '700' }}>Grade</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map((entry, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '6px 10px', fontWeight: '600', color: '#1e293b' }}>
                              {entry.subjectName || entry.subject?.name}
                            </td>
                            <td style={{ padding: '6px 10px', textAlign: 'center', fontWeight: '700' }}>
                              {entry.obtainedMarks} / {entry.maxMarks}
                            </td>
                            <td style={{ padding: '6px 10px', textAlign: 'center', fontWeight: '800', color: '#2563eb' }}>
                              {entry.grade || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                </div>
              )}

              {/* TAB 2: MARKS DETAIL */}
              {activeTab === 'marks' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    Complete subject breakdown and components for {rc.term}:
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {entries.map((entry, idx) => (
                      <div key={idx} style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '12px',
                        background: '#ffffff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '800', color: '#0f172a' }}>
                            {entry.subjectName || entry.subject?.name}
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                            Type: {entry.subjectType || 'Core'} | Max: {entry.maxMarks}
                          </div>
                        </div>

                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '16px', fontWeight: '800', color: '#1e3a8a' }}>
                            {entry.obtainedMarks} <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 'normal' }}>/ {entry.maxMarks}</span>
                          </div>
                          <span style={{
                            fontSize: '11px',
                            fontWeight: '800',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            background: entry.grade === 'F' ? '#fee2e2' : '#dcfce7',
                            color: entry.grade === 'F' ? '#dc2626' : '#166534'
                          }}>
                            {entry.grade || '—'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* TAB 3: REMARKS */}
              {activeTab === 'remarks' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                      Class Teacher Remarks
                    </label>
                    <textarea
                      value={classRemarks}
                      onChange={(e) => setClassRemarks(e.target.value)}
                      placeholder="Enter teacher feedback, behavior, strengths..."
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        fontSize: '12.5px',
                        borderRadius: '8px',
                        border: '1.5px solid #cbd5e1',
                        minHeight: '90px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                      Principal / Head of Institution Remarks
                    </label>
                    <textarea
                      value={principalRemarks}
                      onChange={(e) => setPrincipalRemarks(e.target.value)}
                      placeholder="Enter official sign-off comments..."
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        fontSize: '12.5px',
                        borderRadius: '8px',
                        border: '1.5px solid #cbd5e1',
                        minHeight: '90px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleSaveRemarks}
                    disabled={savingRemarks}
                    style={{
                      padding: '10px',
                      fontSize: '13px',
                      fontWeight: '800',
                      background: '#2563eb',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '7px',
                      cursor: savingRemarks ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <Check size={15} /> {savingRemarks ? 'Saving Remarks...' : 'Save Remarks'}
                  </button>
                </div>
              )}
            </>
          )}

        </div>

        {/* Footer Actions */}
        <div style={{
          padding: '14px 20px',
          borderTop: '1.5px solid #e2e8f0',
          background: '#f8fafc',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px'
        }}>
          <button
            type="button"
            onClick={() => onOpenFullPreview(rc)}
            style={{
              padding: '8px 14px',
              fontSize: '12.5px',
              fontWeight: '700',
              background: '#2563eb',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Eye size={13} /> Full Preview
          </button>

          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              type="button"
              onClick={() => onDownloadPDF(rc)}
              style={{
                padding: '8px 12px',
                fontSize: '12.5px',
                fontWeight: '700',
                background: '#ffffff',
                color: '#334155',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              <Download size={13} /> PDF
            </button>

            {rc.status === 'DRAFT' && (
              <button
                type="button"
                onClick={() => onPublish(rc.id)}
                style={{
                  padding: '8px 14px',
                  fontSize: '12.5px',
                  fontWeight: '700',
                  background: '#16a34a',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <Send size={13} /> Publish
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default ReportCardStudentDrawer;
